<template>
  <Dialog
    :open="open"
    size="2"
    class="hp-dialog-reset"
    @update:open="$emit('update:open', $event)"
    @close="handleClose"
  >
    <DialogHeader>
      <DialogTitle>{{ $t('modules.campaigns.shared.selectBannerType') }}</DialogTitle>
      <DialogControls>
        <DialogClose />
      </DialogControls>
    </DialogHeader>

    <DialogBody :id="dialogContentId" padded>
      <div v-if="error" class="banner-format-error">
        {{ error }}
      </div>
      <div v-else class="banner-format-select">
        <Select
          v-model="selectedFormatIdModel"
          :options="formatOptions"
          :disabled="isLoading || isLoadingFormats"
          :placeholder="isLoadingFormats ? $t('modules.campaigns.bannerFormat.loadingFormats') : $t('modules.campaigns.bannerFormat.selectFormat')"
          :clearable="false"
        />
      </div>
    </DialogBody>

    <DialogFooter>
      <Button variant="outline" :disabled="isLoading" @click="handleClose">{{ $t('modules.campaigns.shared.cancel') }}</Button>
      <Button
        variant="filled"
        :disabled="!selectedFormatId || isLoading || isLoadingFormats || !!error"
        :loading="isLoading"
        @click="handleCreate"
      >
        {{ $t('modules.campaigns.shared.createBanner') }}
      </Button>
    </DialogFooter>
  </Dialog>
</template>

<script setup lang="ts">
import { useAdUnitTypes } from '@hprofits/api-client';
import { AdFormatId, AdFormatIdSchema, AdTypeSchema } from '@hprofits/shared';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogControls,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '@hprofits/ui';
import { formatDimensionSize } from '@hprofits/utils';
import { hasValue } from '@hprofits/utils/fp/optional';
import { computed, ref, toRef, watch } from 'vue';

import { apiClient } from '@/api-client';
import { BannerFormatSelection } from '@/modules/campaigns/composables/actions/useCreativeTypeFlow';

// TODO: This component looks like over-engineering: we already have generic dialogs. Also it would be nice to make dialog look like AdGroup: form with title editing.

const dialogContentId = 'banner-format-dialog';
const DESKTOP_FOOTER_FORMAT = 'Desktop Footer (300x250)';
const DESKTOP_WIDE_FOOTER_FORMAT = 'Desktop Wide Footer (900x250)';
const ITVB_FORMAT = 'ITVB (300x250)';
const ITVA_FORMAT = 'ITVA (300x250)';

type BannerFormatItem = {
  readonly name: string;
};

const props = defineProps<{
  open?: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  select: [selection: BannerFormatSelection];
  cancel: [];
}>();

const { data: adUnitTypes, isLoading: isLoadingFormats, error } = useAdUnitTypes(apiClient, 'full');
const selectedFormatId = ref<AdFormatId | null>(null);
const bannerTypeId = computed(() => {
  if (hasValue(adUnitTypes.value)) {
    return adUnitTypes.value.find((v) => v.name === AdTypeSchema.enum.Banner)?.id;
  }
  return undefined;
});
const formats = computed(() => {
  if (hasValue(adUnitTypes.value)) {
    return adUnitTypes.value.find((v) => v.name === AdTypeSchema.enum.Banner)?.formats ?? [];
  }
  return [];
});

const moveFormatBefore = <TFormat extends BannerFormatItem>(
  items: ReadonlyArray<TFormat>,
  movedName: string,
  targetName: string,
): Array<TFormat> => {
  const result = [...items];
  const movedIndex = result.findIndex((item) => item.name.trim() === movedName);
  const targetIndex = result.findIndex((item) => item.name.trim() === targetName);
  const movedItem = result[movedIndex];
  if (movedItem === undefined || targetIndex < 0) {
    return result;
  }
  result.splice(movedIndex, 1);
  const nextTargetIndex = result.findIndex((item) => item.name.trim() === targetName);
  result.splice(nextTargetIndex, 0, movedItem);
  return result;
};

const orderedFormats = computed(() =>
  moveFormatBefore(
    moveFormatBefore(formats.value, DESKTOP_FOOTER_FORMAT, DESKTOP_WIDE_FOOTER_FORMAT),
    ITVB_FORMAT,
    ITVA_FORMAT,
  ),
);

const selectedFormatIdModel = computed({
  get: () => selectedFormatId.value ?? undefined,
  set: (v: number | string | undefined) => {
    selectedFormatId.value = hasValue(v) && v !== '' ? AdFormatIdSchema.parse(v) : null;
  },
});

const formatOptions = computed(() =>
  orderedFormats.value.map((f) => ({ value: f.id, label: formatDimensionSize(f.name) })),
);

watch(toRef(props, 'open'), async (open) => {
  if (open) {
    const firstFormat = orderedFormats.value[0];
    if (firstFormat) {
      selectedFormatId.value = firstFormat.id;
    }
  }
});

const handleClose = (): void => {
  if (!props.isLoading) {
    emit('cancel');
  }
};

const handleCreate = (): void => {
  const format = orderedFormats.value.find((f) => f.id === selectedFormatId.value);
  if (format && bannerTypeId.value) {
    emit('select', { typeId: bannerTypeId.value, format });
  }
};
</script>

<style scoped lang="scss">
.banner-format-error {
  padding: var(--space-4);
  color: var(--error-fg);
  background: var(--error-bg);
  border-radius: var(--border-radius-input);
  font-size: 14px;
  text-align: center;
}

.banner-format-select {
  width: 100%;
}
</style>
